/**
 * Estado global de la GERENCIA ACTIVA del PMIS (multi-gerencia F1).
 *
 * Persistencia: localStorage clave `pmis.gerenciaActiva`. La selección sobrevive
 * a reload y a logout/login (el usuario suele volver a la misma gerencia).
 *
 * Suscripción: `useSyncExternalStore` para que los componentes se re-renderen
 * cuando cambie. Patrón consistente con `usePermissions` y la matriz de
 * autoridades.
 *
 * Decisión de modelado:
 *   - La "gerencia activa" es UNA sola a la vez (no es un array de filtros).
 *   - Si el usuario cambia de gerencia, todas las queries se invalidan y se
 *     vuelven a poblar con datos de la nueva gerencia.
 *   - Default: primera gerencia del array `usuario.gerenciasAccesibles` al login, o "CO" si
 *     no hay info disponible.
 */

import { useSyncExternalStore } from "react";
import type { GerenciaCodigo, GerenciaInfoEditable } from "@constants/gerencias";
import {
  GERENCIA_DEFAULT,
  GERENCIAS_POR_CODIGO,
  leerGerenciasCatalogo,
} from "@constants/gerencias";

const STORAGE_KEY = "pmis.gerenciaActiva";
const EVENT_NAME = "pmis:gerenciaActiva:changed";

// ════════════════════════════════════════════════════════════════════
// Migración one-shot: keys viejas (multi-país) → nuevas (multi-gerencia)
// ════════════════════════════════════════════════════════════════════
//
// Se ejecuta una sola vez al cargar el módulo. Para cada par (vieja, nueva):
//   - Si la NUEVA ya existe, no se toca (el usuario ya migró).
//   - Si la VIEJA existe y la NUEVA no, se copia el valor y se borra la vieja.
//
// Esto preserva la selección del switcher, el flag "ver todos" y el catálogo
// editado del ADMIN_PMIS sin requerir relogueo ni perder datos.
function migrarStorageKeys(): void {
  if (typeof window === "undefined") return;
  try {
    const map: Array<[string, string]> = [
      ["pmis.paisActivo", "pmis.gerenciaActiva"],
      ["pmis.verTodosLosPaises", "pmis.verTodasLasGerencias"],
      ["pmis.paisesCatalogo", "pmis.gerenciasCatalogo"],
    ];
    for (const [vieja, nueva] of map) {
      if (localStorage.getItem(nueva) !== null) continue;
      const v = localStorage.getItem(vieja);
      if (v !== null) {
        localStorage.setItem(nueva, v);
        localStorage.removeItem(vieja);
      }
    }
  } catch {
    // localStorage puede tirar (modo privado, quota). Mejor seguir sin migrar
    // que reventar el arranque.
  }
}
migrarStorageKeys();

const listeners = new Set<() => void>();

function notificar(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_NAME));
  }
  for (const l of listeners) l();
}

/** Lee la gerencia activa desde localStorage. Si no es válido, devuelve el default. */
export function leerGerenciaActiva(): GerenciaCodigo {
  try {
    const raw =
      typeof window !== "undefined" ? localStorage.getItem(STORAGE_KEY) : null;
    if (raw && raw in GERENCIAS_POR_CODIGO) {
      return raw as GerenciaCodigo;
    }
  } catch {
    // localStorage puede tirar (modo privado, quota, etc.).
  }
  return GERENCIA_DEFAULT;
}

/**
 * Establece la gerencia activa. Si el código no existe en el catálogo, no hace
 * nada (defensa contra IDs externos inválidos). Notifica suscriptores.
 */
export function establecerGerenciaActiva(gerenciaId: GerenciaCodigo): void {
  if (!(gerenciaId in GERENCIAS_POR_CODIGO)) return;
  try {
    if (typeof window !== "undefined") {
      localStorage.setItem(STORAGE_KEY, gerenciaId);
    }
  } catch {
    // ignore
  }
  notificar();
}

/**
 * Hook React que devuelve la gerencia activa y se re-renderiza cuando cambia.
 * Patrón `useSyncExternalStore` — concurrent-safe en React 18+.
 */
export function useGerenciaActiva(): GerenciaCodigo {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      const handler = () => cb();
      if (typeof window !== "undefined") {
        window.addEventListener(EVENT_NAME, handler);
      }
      return () => {
        listeners.delete(cb);
        if (typeof window !== "undefined") {
          window.removeEventListener(EVENT_NAME, handler);
        }
      };
    },
    leerGerenciaActiva,
    () => GERENCIA_DEFAULT, // SSR fallback
  );
}

// ════════════════════════════════════════════════════════════════════
// Modo INTER-GERENCIA (vista cross-gerencia de solo lectura)
// ════════════════════════════════════════════════════════════════════
//
// Por defecto el PMIS filtra todo por la GERENCIA ACTIVA (modo gestión).
// Activar este flag muestra datos de TODAS las gerencias a las que el usuario
// tiene acceso, en modo READ-ONLY: el usuario puede ver lecciones aprendidas,
// riesgos, eventos, KPIs de otras gerencias para enriquecer conocimiento,
// pero NO puede editar/crear/borrar entidades fuera de su gerencia activa.
//
// La IA NO se ve afectada por este flag — los helpers de iaSugerencias.ts
// leen los repos directamente, así que siempre operan global aunque el
// usuario esté en modo gestión.

const STORAGE_KEY_VER_TODOS = "pmis.verTodasLasGerencias";
const EVENT_VER_TODOS = "pmis:verTodasLasGerencias:changed";
const listenersVerTodos = new Set<() => void>();

function notificarVerTodos(): void {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent(EVENT_VER_TODOS));
  }
  for (const l of listenersVerTodos) l();
}

export function leerVerTodasLasGerencias(): boolean {
  try {
    const raw =
      typeof window !== "undefined"
        ? localStorage.getItem(STORAGE_KEY_VER_TODOS)
        : null;
    return raw === "1";
  } catch {
    return false;
  }
}

export function establecerVerTodasLasGerencias(activo: boolean): void {
  try {
    if (typeof window !== "undefined") {
      if (activo) localStorage.setItem(STORAGE_KEY_VER_TODOS, "1");
      else localStorage.removeItem(STORAGE_KEY_VER_TODOS);
    }
  } catch {
    // ignore
  }
  notificarVerTodos();
}

/**
 * Hook: ¿está activo el modo INTER-GERENCIA (vista cross-gerencia de solo lectura)?
 *
 * Cuando es `true`:
 *   - Los `useBudgets()` / `useAfes()` traen TODO sin filtrar por gerencia.
 *   - Los list pages muestran un banner amarillo "Vista inter-gerencia".
 *   - Las acciones de editar/crear/borrar siguen disponibles solo para
 *     items que pertenecen a la `gerenciaActiva` (la UI lo controla pieza por
 *     pieza). La IA opera global siempre, independiente de este flag.
 */
// ════════════════════════════════════════════════════════════════════
// Catálogo de gerencias (F4) — versión reactiva del GERENCIAS_DEFAULT/localStorage
// ════════════════════════════════════════════════════════════════════
//
// El catálogo se persiste en localStorage; los componentes que lo consumen
// (GerenciaSwitcher, GerenciasConfigPage) se re-renderan cuando el ADMIN_PMIS
// edita la lista, gracias a este hook.

const EVENT_CATALOGO = "pmis:gerenciasCatalogo:changed";

// Cache estable del snapshot. `useSyncExternalStore` compara con `Object.is`
// — si devolvemos un array nuevo cada llamada (como hace `leerGerenciasCatalogo`),
// React entra en bucle de re-renders ("getSnapshot should be cached" → app
// en blanco). Cacheamos la referencia y la invalidamos solo cuando se
// dispara el evento de cambio.
let _snapshotCatalogo: GerenciaInfoEditable[] | null = null;

function getSnapshotCatalogo(): GerenciaInfoEditable[] {
  if (_snapshotCatalogo === null) {
    _snapshotCatalogo = leerGerenciasCatalogo();
  }
  return _snapshotCatalogo;
}

if (typeof window !== "undefined") {
  // Invalidamos el cache al cambiar el catálogo desde GerenciasConfigPage.
  window.addEventListener(EVENT_CATALOGO, () => {
    _snapshotCatalogo = null;
  });
}

/** Hook que devuelve el catálogo de gerencias editable (sólo los activos). */
export function useGerenciasCatalogo(): GerenciaInfoEditable[] {
  return useSyncExternalStore(
    (cb) => {
      const handler = () => cb();
      if (typeof window !== "undefined") {
        window.addEventListener(EVENT_CATALOGO, handler);
      }
      return () => {
        if (typeof window !== "undefined") {
          window.removeEventListener(EVENT_CATALOGO, handler);
        }
      };
    },
    getSnapshotCatalogo,
    getSnapshotCatalogo,
  );
}

export function useVerTodasLasGerencias(): boolean {
  return useSyncExternalStore(
    (cb) => {
      listenersVerTodos.add(cb);
      const handler = () => cb();
      if (typeof window !== "undefined") {
        window.addEventListener(EVENT_VER_TODOS, handler);
      }
      return () => {
        listenersVerTodos.delete(cb);
        if (typeof window !== "undefined") {
          window.removeEventListener(EVENT_VER_TODOS, handler);
        }
      };
    },
    leerVerTodasLasGerencias,
    () => false,
  );
}
